import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Building2,
  FileText,
  HelpCircle,
  Home,
  MessageSquare,
  Receipt,
  Search,
  ShieldCheck,
  Wrench,
} from 'lucide-react';

import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const tenantFeatures = [
  {
    icon: Search,
    title: 'Before you become a tenant',
    description: 'Browse listings, save homes, apply, and track application status from your tenant dashboard.',
  },
  {
    icon: FileText,
    title: 'When an application is approved',
    description: 'Approval means the landlord still needs to send you an invite before your tenancy becomes active.',
  },
  {
    icon: MessageSquare,
    title: 'After your tenancy is active',
    description: 'Use your dashboard for documents, active-tenancy messages, maintenance issues, rent visibility, and receipts.',
  },
  {
    icon: Wrench,
    title: 'Know where each conversation lives',
    description: 'Application conversations stay with Applications. Active tenancy messages appear in Message Center.',
  },
];

const landlordFeatures = [
  {
    icon: Home,
    title: 'Set up the property first',
    description: 'Create the property record before listing, tenant management, documents, rent, or written statements.',
  },
  {
    icon: Building2,
    title: 'Turn applicants into tenants clearly',
    description: 'Approve only records your decision. Invite sends a portal link; the tenancy activates after the tenant accepts. For offline occupants, the landlord adds a manual tenancy instead.',
  },
  {
    icon: Receipt,
    title: 'Use tenancy-aware operations',
    description: 'Create rent schedules, receipts, messages, documents, and written statements against the correct tenancy.',
  },
  {
    icon: ShieldCheck,
    title: 'Run MOT checks with context',
    description: 'Use Property MOT to review missing, expired, or tenancy-sensitive records before key decisions.',
  },
];

const tenantFaqs = [
  {
    question: 'Am I a tenant as soon as my application is approved?',
    answer:
      'No. Approval means the landlord wants to move forward. You become an active tenant only after the landlord sends you an invite that you accept.',
  },
  {
    question: 'Where do I track my application?',
    answer:
      'Use your tenant dashboard. It shows pending, approved, and rejected applications so you do not need to return to Explore just to check status.',
  },
  {
    question: 'What happens if I receive an invite?',
    answer:
      'Use the invite link from your email. If you need to register first, SIYAK keeps the invite context through email verification and then takes you to your active tenancy dashboard after acceptance.',
  },
  {
    question: 'Where do I find tenancy documents?',
    answer:
      'After your tenancy is active, open the Documents tab on your tenant dashboard. Signed statements, landlord-shared documents, and receipts appear there when available.',
  },
  {
    question: 'Where are messages shown?',
    answer:
      'Application conversations are shown with Applications. Message Center is for active-tenancy conversations after the tenancy relationship exists.',
  },
  {
    question: 'How do I report a maintenance issue?',
    answer:
      'Use the issue reporting flow from your tenant area. This keeps the issue visible to both sides and makes status tracking easier.',
  },
];

const landlordFaqs = [
  {
    question: 'What happens after I register as a landlord?',
    answer:
      'You first verify your email, then your account is reviewed for approval. After approval, you can log in and access the landlord dashboard.',
  },
  {
    question: 'What is the difference between approving and inviting?',
    answer:
      'Approve records your application decision only. Use Invite to send the applicant a portal link; the tenancy activates after they accept. For offline occupants, add a manual tenancy instead.',
  },
  {
    question: 'How do I know whether the tenant has been contacted?',
    answer:
      'The invite panel distinguishes Invite created from Invite email sent and shows the recipient email. Creating an invite link alone does not mean the tenant has been emailed.',
  },
  {
    question: 'Can I use manual tenancies?',
    answer:
      'Yes. Manual tenancies can be used for offline occupants, rent schedules, and written statements. Manual-tenancy document uploads are stored at property level, so use clear labels with tenant names or dates.',
  },
  {
    question: 'What should I check before marking rent paid?',
    answer:
      'Preview the receipt and check Account Settings first. Receipts use your current issuer details, including landlord or company name, address, contact details, and logo.',
  },
  {
    question: 'When should I use the MOT Check?',
    answer:
      'Use Property MOT after the property record exists, and again when tenancy details or documents change. It helps you review missing, expired, or tenancy-sensitive records in the right property context.',
  },
];

const tenantJourney = [
  {
    step: 'Register and verify your email',
    detail: 'Create a tenant account and verify your email before using your tenant dashboard.',
  },
  {
    step: 'Browse, save, and apply',
    detail: 'Use Explore to browse listings, save properties, and apply when a property is accepting applications.',
  },
  {
    step: 'Track application status from your dashboard',
    detail: 'Pending means the landlord has not decided. Approved means the landlord still needs to invite you before your tenancy becomes active. Rejected means the application was not taken forward.',
  },
  {
    step: 'Accept an invite if needed',
    detail: 'If the landlord sends an invite, follow the email link. After acceptance, SIYAK confirms the property you joined and opens your active tenancy dashboard.',
  },
  {
    step: 'Use active-tenancy tools',
    detail: 'Once active, use Documents, Messages, Issues, rent visibility, and receipts from My Tenancy.',
  },
];

const landlordJourney = [
  {
    step: 'Register and verify your email',
    detail: 'Create a landlord account, verify your email address, and wait for admin approval.',
  },
  {
    step: 'Open your dashboard after approval',
    detail: 'Once approved, sign in to access landlord tools and start setting up your working space.',
  },
  {
    step: 'Add properties before tenancy work',
    detail: 'Create the property record first. Listings, documents, tenant management, rent, and statements all depend on the property context.',
  },
  {
    step: 'Review and decide on applications',
    detail: 'Reply, screen, approve, or reject applications. Approval does not create a tenancy.',
  },
  {
    step: 'Invite or add manual tenancy after approval',
    detail: 'Use Create Tenancy / Invite Tenant to send a portal invite, or add a manual tenancy for offline occupants.',
  },
  {
    step: 'Run tenancy operations',
    detail: 'After the tenancy exists, use written statements, rent schedules, receipts, documents, messages, issues, and Property MOT checks.',
  },
];

const tenantFeatureFlows = [
  {
    title: 'Search, save, and apply',
    intro: 'Use this path while you are still an applicant and not yet an active tenant.',
    steps: [
      'Open Explore from your tenant account.',
      'Search or filter listings and open a property to review the details.',
      'Save properties you want to compare later.',
      'Apply from the property page when applications are available.',
      'Return to your tenant dashboard to see saved properties and application status.',
    ],
  },
  {
    title: 'Understand application status',
    intro: 'The dashboard tells you whether you are still an applicant or have moved into an active tenancy.',
    steps: [
      'Pending means the landlord has not made a decision yet.',
      'Approved means the landlord still needs to send you an invite before the tenancy becomes active.',
      'Rejected means the application was not taken forward.',
      'If you receive an invite, complete the invite flow from the email link.',
      'After acceptance, your dashboard changes from pre-tenancy status to active tenancy tools.',
    ],
  },
  {
    title: 'Find the right messages',
    intro: 'Application messages and active-tenancy messages are separate on purpose.',
    steps: [
      'Use Applications for conversations before a tenancy exists.',
      'Use Message Center only after your tenancy is active.',
      'Open message notifications to return to the relevant thread.',
      'Keep tenancy questions in the active-tenancy thread so the record stays tied to the property.',
    ],
  },
  {
    title: 'Use active-tenancy tools',
    intro: 'These tools become useful once the landlord has created your active tenancy.',
    steps: [
      'Open Documents to view landlord-shared files, signed statements, and rent receipts.',
      'Open Messages for active-tenancy conversations.',
      'Use Issues to report maintenance clearly and track progress.',
      'Use rent and receipt areas to view payment history once the landlord has created records.',
    ],
  },
];

const landlordFeatureFlows = [
  {
    title: 'Set up a property',
    intro: 'Start here before listing, applications, tenants, rent, documents, or written statements.',
    steps: [
      'Open the landlord dashboard and add the property.',
      'Complete the core address, rent, and property details.',
      'Use Listing Builder only when you want the property to appear to tenants.',
      'Keep occupied or unavailable properties unlisted so tenants cannot apply by mistake.',
      'Use the property record as the anchor for documents, tenants, rent, and statements.',
    ],
  },
  {
    title: 'Move from application to tenancy',
    intro: 'Approval is only a decision. Tenancy activation is a separate step.',
    steps: [
      'Review the application and reply or screen if needed.',
      'Approve the application only when you want to move forward.',
      'Click Create Tenancy / Invite Tenant to send a portal invite link to the applicant.',
      'Check the invite panel: Invite created means the link exists; Invite email sent means the tenant has been contacted.',
    ],
  },
  {
    title: 'Create statements, rent, and receipts',
    intro: 'These flows depend on the right tenancy context.',
    steps: [
      'Create or confirm the portal or manual tenancy first.',
      'Open Written Statement from Tenancy Management so the statement is tenancy-scoped.',
      'Generate the PDF before sending for signature.',
      'Create rent schedules against the correct portal tenant or manual tenancy.',
      'Preview receipts and check Account Settings before marking rent paid.',
    ],
  },
  {
    title: 'Run a Property MOT check',
    intro: 'Use MOT checks to review property and tenancy record gaps before they become operational risks.',
    steps: [
      'Create or open the property record first.',
      'Open Property MOT for that property.',
      'Review missing, expired, or tenancy-sensitive records.',
      'Upload or update the relevant documents from the property or tenancy document area.',
      'Run the check again after document, tenant, or tenancy details change.',
    ],
  },
  {
    title: 'Manage documents, messages, and manual tenancies',
    intro: 'Use scope carefully so records make sense later.',
    steps: [
      'Use Message Center for active-tenancy messages only; application conversations remain in Applications.',
      'Upload portal-tenancy documents against the selected tenancy where available.',
      'For manual tenancies, documents are stored at property level, so label them with tenant names or dates.',
      'Use the public Send Documents tool when you need a delivery record for served documents.',
    ],
  },
];

function scrollToSection(sectionId) {
  const element = document.getElementById(sectionId);
  if (!element) return;
  const top = element.getBoundingClientRect().top + window.scrollY - 80;
  window.scrollTo({ top, behavior: 'smooth' });
}

function FeatureGrid({ items }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <Card key={item.title} className="border-0 shadow-sm bg-white">
            <CardContent className="p-6 flex gap-4">
              <div className="h-11 w-11 rounded-xl bg-royal-100 text-royal-600 flex items-center justify-center shrink-0">
                <Icon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-navy-900 mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function FaqAccordion({ items, valuePrefix }) {
  return (
    <Accordion type="single" collapsible className="w-full rounded-2xl border bg-white px-5 shadow-sm">
      {items.map((item, index) => (
        <AccordionItem key={item.question} value={`${valuePrefix}-${index}`}>
          <AccordionTrigger className="text-base text-navy-900">{item.question}</AccordionTrigger>
          <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
            {item.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function JourneySteps({ items, accentClass }) {
  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <Card key={item.step} className="border-0 shadow-sm bg-white">
          <CardContent className="p-5 flex gap-4">
            <div className={`h-9 w-9 rounded-full text-white flex items-center justify-center shrink-0 text-sm font-semibold ${accentClass}`}>
              {index + 1}
            </div>
            <div>
              <h3 className="font-semibold text-navy-900 mb-1">{item.step}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{item.detail}</p>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function DetailedFeatureFlows({ title, items, accentClass, valuePrefix }) {
  return (
    <Card className="border-0 shadow-sm bg-white">
      <CardHeader>
        <CardTitle className="text-navy-900">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="w-full">
          {items.map((item, index) => (
            <AccordionItem key={item.title} value={`${valuePrefix}-${index}`}>
              <AccordionTrigger className="text-base text-navy-900">{item.title}</AccordionTrigger>
              <AccordionContent className="space-y-4">
                <p className="text-sm text-muted-foreground leading-relaxed">{item.intro}</p>
                <div className="space-y-3">
                  {item.steps.map((step, stepIndex) => (
                    <div key={`${item.title}-${stepIndex}`} className="flex gap-3 rounded-xl bg-slate-50 p-3">
                      <div className={`h-7 w-7 rounded-full text-white flex items-center justify-center shrink-0 text-xs font-semibold ${accentClass}`}>
                        {stepIndex + 1}
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  );
}

export const UserGuide = () => {
  useEffect(() => {
    document.title = 'User Guide - SIYAK';
  }, []);

  return (
    <Layout>
      <section className="relative overflow-hidden bg-navy-900">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-royal-900" />
        <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.22em] text-amber-400">
              SIYAK portal guide
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              Exact SIYAK journeys for tenants and landlords
            </h1>
            <p className="text-lg leading-relaxed text-navy-200 mb-8">
              Use this page when you need to know what happens next across applications, approvals, invites,
              active tenancies, signing, documents, messages, rent, and receipts.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button
                size="lg"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => scrollToSection('tenant-guide')}
              >
                <Home className="mr-2 h-5 w-5" />
                Tenant guide
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white/25 bg-white/10 text-white hover:bg-white/20"
                onClick={() => scrollToSection('landlord-guide')}
              >
                <Building2 className="mr-2 h-5 w-5" />
                Landlord guide
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 space-y-10">
        <section>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-navy-900">
                  <BookOpen className="h-5 w-5 text-royal-600" />
                  What this guide covers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                <p>
                  This guide follows the real SIYAK journeys in order, so tenants can tell whether they are
                  applying or actively renting, and landlords can see when to approve, invite, send,
                  sign, upload, message, and create receipts.
                </p>
                <p>
                  It is intended as a practical portal guide. It does not replace legal, regulatory, or tax advice.
                </p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm bg-royal-50">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-2 text-royal-700 font-semibold">
                  <HelpCircle className="h-5 w-5" />
                  Quick start
                </div>
                <div className="space-y-2 text-sm text-navy-800">
                  <p>1. Create your account</p>
                  <p>2. Verify your email</p>
                  <p>3. Open your dashboard</p>
                  <p>4. Use the role guide below</p>
                </div>
                <Button asChild className="w-full">
                  <Link to="/register">
                    Create account
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        <section id="tenant-guide" className="space-y-5">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-green-100 text-green-700 flex items-center justify-center">
              <Home className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-navy-900">Tenant guide</h2>
              <p className="text-sm text-muted-foreground">Clear next steps for applicants and active tenants.</p>
            </div>
          </div>
          <FeatureGrid items={tenantFeatures} />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-0 shadow-sm bg-navy-50">
            <CardHeader>
              <CardTitle className="text-navy-900">Tenant journey</CardTitle>
            </CardHeader>
            <CardContent>
              <JourneySteps items={tenantJourney} accentClass="bg-green-600" />
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-navy-900">Tenant Q&amp;A</CardTitle>
            </CardHeader>
            <CardContent>
              <FaqAccordion items={tenantFaqs} valuePrefix="tenant" />
            </CardContent>
          </Card>
        </section>

        <section>
          <DetailedFeatureFlows
            title="Tenant flows"
            items={tenantFeatureFlows}
            accentClass="bg-green-600"
            valuePrefix="tenant-flow"
          />
        </section>

        <section id="landlord-guide" className="space-y-5">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-navy-900">Landlord guide</h2>
              <p className="text-sm text-muted-foreground">Clear next steps for property setup, applications, and tenancy work.</p>
            </div>
          </div>
          <FeatureGrid items={landlordFeatures} />
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-0 shadow-sm bg-navy-50">
            <CardHeader>
              <CardTitle className="text-navy-900">Landlord journey</CardTitle>
            </CardHeader>
            <CardContent>
              <JourneySteps items={landlordJourney} accentClass="bg-amber-600" />
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-navy-900">Landlord Q&amp;A</CardTitle>
            </CardHeader>
            <CardContent>
              <FaqAccordion items={landlordFaqs} valuePrefix="landlord" />
            </CardContent>
          </Card>
        </section>

        <section>
          <DetailedFeatureFlows
            title="Landlord flows"
            items={landlordFeatureFlows}
            accentClass="bg-amber-600"
            valuePrefix="landlord-flow"
          />
        </section>

        <section>
          <Card className="border-0 shadow-md bg-navy-900 text-white">
            <CardContent className="p-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="font-heading text-2xl font-bold mb-2">Need more help?</h2>
                <p className="text-navy-200 text-sm leading-relaxed max-w-2xl">
                  Start with this guide, then use the contact page if you need further help with using the portal.
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button asChild className="bg-amber-500 hover:bg-amber-600 text-white">
                  <Link to="/contact">Contact us</Link>
                </Button>
                <Button asChild variant="outline" className="border-white/30 bg-white/10 text-white hover:bg-white/20">
                  <Link to="/login">Portal Access</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </Layout>
  );
};
